import { useParams } from 'react-router-dom';
import {
  CheckboxInput,
  Flex, FlexColumn, Icon, LinkButton, LockIcon, MultiSelectInput,
  SecondaryButton,
  useAsyncFunctionProgress,
  useIsMsalAuth,
  useIsUserAuth,
  UserRoles,
  useStyledTheme,
  useUserHasRole,
} from '@equifinance/shared';
import styled, { css } from 'styled-components';
import {
  ReactNode,
  useEffect, useMemo, useRef, useState,
} from 'react';
import { orderBy } from 'lodash';
import moment from 'moment';
import Section from '../../Account/components/Section';
import TaskDetailComponent from '../components/TaskDetails';
import {
  useTasksContext, Stage, Progress, Category,
  Task,
} from '../../../contexts/tasks';
import { TasksRefNames } from '../TasksSidebar';
import { CrossCircleIcon, PlusIcon, TickCircleIconInverted } from '../../Account/components/Icons';
import { usePageConfigurationContext } from '../../../contexts/pageConfiguration';
import { Account, useAccountContext } from '../../../contexts';

const TaskControlPanelContainer = styled(Flex)`${({ theme }) => `
  justify-content: space-between;
  gap: ${theme.padding[2]};
  padding: 0 ${theme.padding[6]};
`}`;

const TaskFilterContainer = styled(Flex)`${({ theme }) => `
  gap: ${theme.padding[2]};
  flex-wrap: wrap;
`}`;

const TasksContainer = styled(FlexColumn)`${({ theme }) => `
  margin-top: ${theme.padding[4]};
  gap: ${theme.padding[1]};
`}`;

const TasksHeader = styled(Flex)`${({ theme }) => `
  background-color: ${theme.colours.primary[10]};
  padding: ${theme.padding[3]} 0;
`}`;

const TasksHeaderStatus = styled.p`${({ theme }) => `
  width: ${theme.taskStatusWidth};
  display: flex;
  justify-content: center;
  color: ${theme.colours.primary[1]};
  font-size: ${theme.fontSize[1]};
  font-weight: ${theme.fontWeight.bold};
`}`;

const TasksHeaderDescription = styled.p`${({ theme }) => `
  color: ${theme.colours.primary[1]};
  font-size: ${theme.fontSize[1]};
  font-weight: ${theme.fontWeight.bold};
  padding: 0 ${theme.padding[6]};
`}`;

export const TaskUnlockIcon = styled(Icon).attrs(() => ({
  className: 'mi-unlock',
}))` ${({ theme }) => `
  color: ${theme.colours.primary[1]};
  font-size:${theme.fontSize[3]};
`}`;

const taskFilterCss = css` ${({ theme }) => `
  color: ${theme.colours.neutral[2]};
  .dropdown-container, .multi-select-parent {
    height: ${theme.padding[8]};
    font-size: ${theme.fontSize[1]};
    font-weight: ${theme.fontWeight.bold};
  }
  .multi-select-parent {
    min-width: auto;
  }
  .dropdown-container {
    border-radius: ${theme.padding[2]};
    border: ${theme.borderWidth[1]} solid ${theme.colours.neutral[6]};
  }
  .mi-chevron-up, .mi-chevron-down {
    font-size: ${theme.fontSize[4]};
  }
  .dropdown-content {
    min-width: ${theme.dropdownWidth};
  }
  .mi-close {
    font-size: ${theme.fontSize[3]};
    display: flex;
  }
`}`;

const AddButton = styled(SecondaryButton) <{ disabled?: boolean }>`${({ theme, disabled }) => `
  * {
    color: ${disabled ? theme.colours.neutral[5] : theme.colours.primary[1]};
  }
`}`;

const CheckboxWrapper = styled(Flex)` ${({ theme }) => `
  margin-left: ${theme.padding[2]};
`}`;

const statusTaskOrderByExternalUser = [
  'Draft',
  'OutstandingForBroker',
  'MoreInformationRequired',
  'PendingSubmission',
  'SubmittedToLender',
  'Rejected',
  'ApprovedWithException',
  'Approved',
];

const statusTaskOrderByInternalUser = [
  'Draft',
  'SubmittedToLender',
  'PendingSubmission',
  'MoreInformationRequired',
  'OutstandingForBroker',
  'Rejected',
  'ApprovedWithException',
  'Approved',
];

export function TaskSectionTitleIcon({
  tasks,
  applicationStatus,
  lockIcon = <LockIcon />,
  tickIcon = <TickCircleIconInverted />,
  crossIcon = <CrossCircleIcon />,
}: {
  tasks: Task[],
  applicationStatus: Account['status'],
  lockIcon?: ReactNode,
  tickIcon?: ReactNode,
  crossIcon?: ReactNode,
}) {
  if (tasks.some((task) => !['ApprovedWithException', 'Approved'].includes(task.status))) {
    return <>{crossIcon}</>;
  }
  if (applicationStatus === 'Complete') {
    return <>{lockIcon}</>;
  }
  return <>{tickIcon}</>;
}

export default function TaskSection() {
  const { id } = useParams() as { id: string };
  const theme = useStyledTheme();
  const [stageFilter, setStageFilter] = useState<string[]>([]);
  const [progressFilter, setProgressFilter] = useState<string[]>([]);
  const [categoryFilter, setCategoryFilter] = useState<string[]>([]);
  const [hasUpdate, setHasUpdate] = useState(false);
  const {
    tasks, expandedStates, handleToggle, setExpandedState,
  } = useTasksContext();
  const { account } = useAccountContext();
  const [filteredTasks, setFilteredTasks] = useState<Task[]>([]);
  const enabledClearFilterBtn = !!(stageFilter.length && progressFilter.length && categoryFilter.length && hasUpdate);

  const isBroker = useIsUserAuth();
  const isSalesSupport = useUserHasRole(UserRoles.SALES_SUPPORT);
  const isInternalUser = useIsMsalAuth();

  const taskOrderByUserRole = useMemo(() => {
    if (isBroker || isSalesSupport) return statusTaskOrderByExternalUser;
    return statusTaskOrderByInternalUser;
  }, [isBroker, isSalesSupport, isInternalUser]);

  const tasksIndices = useMemo(() => tasks?.tasks?.reduce((prev, current, index) => ({
    ...prev,
    [current.id]: index,
  }), {} as Record<string, number>), [tasks]);

  const clearFilters = () => {
    setStageFilter([]);
    setCategoryFilter([]);
    setProgressFilter([]);
    setHasUpdate(false);
  };

  useEffect(() => {
    const filteredOutTasks = tasks?.tasks
      .filter(({ updated }) => !hasUpdate || updated)
      .filter(({ stage }) => stageFilter.length === 0 || stageFilter.includes(stage))
      .filter(({ status }) => progressFilter.length === 0 || progressFilter.includes(status))
      .filter(({ category }) => categoryFilter.length === 0 || categoryFilter.includes(category)) ?? [];

    const sortedTasks = orderBy(
      filteredOutTasks,
      [
        (t) => {
          const idx = taskOrderByUserRole.indexOf(t.status);
          return idx === -1 ? Number.MAX_SAFE_INTEGER : idx;
        },
        (t) => moment(t.lastUpdated).valueOf(),
      ],
      [
        'asc',
        'desc',
      ],
    );

    setFilteredTasks(sortedTasks);
  }, [taskOrderByUserRole, stageFilter, progressFilter, categoryFilter, hasUpdate, tasks]);

  const { createTask } = useTasksContext();

  const [currentTaskIds, setCurrentTaskIds] = useState<string[]>([]);
  const prevTaskIdsRef = useRef<string[]>([]);
  const isFirstAddNewTaskRef = useRef<boolean>(false);
  const { isReadOnly } = usePageConfigurationContext();

  const CategoryOptions = account?.applicants?.length > 1 ? Category : {
    Application: Category.Application,
    Applicant1: Category.Applicant1,
  };

  useEffect(() => {
    const currentTaskIds = tasks?.tasks.map((t) => t?.id ?? '');
    setCurrentTaskIds([...currentTaskIds]);
    if (!isFirstAddNewTaskRef.current) {
      prevTaskIdsRef.current = [...currentTaskIds];
    }
  }, [tasks]);

  useEffect(() => {
    if (!isFirstAddNewTaskRef.current) return;
    const prevTaskIds = prevTaskIdsRef.current;
    const addedTaskIds = currentTaskIds.filter((item) => !prevTaskIds.includes(item));

    addedTaskIds.forEach((taskid) => {
      setExpandedState(taskid, true);
    });

    prevTaskIdsRef.current = [...currentTaskIds];
  }, [currentTaskIds]);

  const onNewTask = async () => {
    if (!isFirstAddNewTaskRef.current) {
      isFirstAddNewTaskRef.current = true;
    }
    await createTask();
  };

  const { inProgress, execute: executeNewTask } = useAsyncFunctionProgress(onNewTask);

  return (
    <Section
      id={`${id}-tasks`}
      refName={TasksRefNames.TASKS}
      title="Tasks"
      titleIcon={<TaskSectionTitleIcon tasks={tasks.tasks} applicationStatus={account.status} />}
      collapsed={!expandedStates[TasksRefNames.TASKS]}
      onClickCollapse={() => handleToggle(TasksRefNames.TASKS)}
    >
      <FlexColumn>
        <TaskControlPanelContainer>
          <TaskFilterContainer>
            <MultiSelectInput
              id="stage-filter"
              value={stageFilter}
              onChange={(value: string[]) => { setStageFilter(value); }}
              selectedLabel="Stage"
              disableSearch
              options={Stage}
              onDeselectOption={(value: string) => {
                setStageFilter((prev: string[]) => prev.filter((s) => s !== value));
              }}
              containerStyles={taskFilterCss}
            />

            <MultiSelectInput
              id="progress-filter"
              value={progressFilter}
              onChange={(value: string[]) => { setProgressFilter(value); }}
              selectedLabel="Task progress"
              disableSearch
              options={Progress}
              onDeselectOption={(value: string) => {
                setProgressFilter((prev: string[]) => prev.filter((s) => s !== value));
              }}
              containerStyles={taskFilterCss}
            />

            <MultiSelectInput
              id="category-filter"
              value={categoryFilter}
              onChange={(value: string[]) => { setCategoryFilter(value); }}
              selectedLabel="Category"
              disableSearch
              options={CategoryOptions}
              onDeselectOption={(value: string) => {
                setCategoryFilter((prev: string[]) => prev.filter((s) => s !== value));
              }}
              containerStyles={taskFilterCss}
            />

            <CheckboxWrapper>
              <CheckboxInput
                id="hasUpdate"
                value={hasUpdate}
                onChange={setHasUpdate}
                label="Has update"
              />
            </CheckboxWrapper>

            {enabledClearFilterBtn && (
              <LinkButton
                small
                onClick={clearFilters}
                color={theme.colours.primary[1]}
              >
                Clear all
              </LinkButton>

            )}
          </TaskFilterContainer>
          <AddButton onClick={executeNewTask} small disabled={isReadOnly || inProgress}>
            <PlusIcon />
            New task
          </AddButton>
        </TaskControlPanelContainer>

        <TasksContainer>
          <TasksHeader>
            <TasksHeaderStatus>Status</TasksHeaderStatus>
            <TasksHeaderDescription>Title and description</TasksHeaderDescription>
          </TasksHeader>

          {filteredTasks?.map((task) => <TaskDetailComponent key={task.id} task={task} taskIndex={tasksIndices[task.id]} />)}

        </TasksContainer>
      </FlexColumn>

    </Section>
  );
}
